import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  SequenceFormData, 
  sequenceSchema, 
  UploadedFile, 
  WizardStep,
  WIZARD_STEPS 
} from '../types';
import { SpotifyTrack } from '@/services/spotifyService';

export const useUploadWizard = (
  onSuccess?: (sequenceId: string) => void,
  onCancel?: () => void
) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [selectedSpotifyTrack, setSelectedSpotifyTrack] = useState<SpotifyTrack | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<SequenceFormData>({
    resolver: zodResolver(sequenceSchema),
    defaultValues: {
      is_free: false,
      price: 0,
      software_type: 'xLights',
      holiday_type: 'Christmas',
    },
  });

  const getCurrentStepId = (): WizardStep => {
    return WIZARD_STEPS[currentStep].id;
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < WIZARD_STEPS.length) {
      setCurrentStep(step);
    }
  };

  const goNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const uploadFileToStorage = useCallback(async (file: File) => {
    if (!user) {
      toast.error('You must be logged in to upload files');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate unique filename
      const fileExtension = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}-${file.name}`;

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload file to Supabase storage
      const { data: fileData, error: fileError } = await supabase.storage
        .from('sequences')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      clearInterval(progressInterval);

      if (fileError) throw fileError;

      setUploadProgress(100);
      
      // Store file info for later use
      setUploadedFile(prev => prev ? Object.assign(prev, { 
        uploadedPath: fileName,
        uploadedSize: file.size 
      }) : null);
      
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file. Please try again.');
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  }, [user]);

  const handleFileSelect = useCallback(async (file: File) => {
    setUploadedFile(file);
    
    // Auto-populate title from filename (remove extension)
    const titleFromFile = file.name.replace(/\.[^/.]+$/, '');
    form.setValue('title', titleFromFile);
    
    // Start file upload
    toast.success('File selected! Starting upload...');
    await uploadFileToStorage(file);
  }, [form, uploadFileToStorage]);

  const handleFileRemove = useCallback(() => {
    setUploadedFile(null);
    setUploadProgress(0);
  }, []);

  const handleSpotifyTrackSelect = useCallback((track: SpotifyTrack | null) => {
    setSelectedSpotifyTrack(track);
  }, []);

  const handleSubmit = async () => {
    if (!user) {
      toast.error('You must be logged in to submit a sequence');
      return;
    }

    if (!uploadedFile?.uploadedPath) {
      toast.error('No file uploaded');
      return;
    }

    const formData = form.getValues();
    
    setIsSubmitting(true);

    try {
      // Create sequence record in database
      const { data: sequence, error: sequenceError } = await supabase
        .from('sequences')
        .insert({
          user_id: user.id,
          title: formData.title,
          artist: formData.artist,
          duration: formData.duration,
          description: formData.description,
          software_type: formData.software_type,
          holiday_type: formData.holiday_type,
          genre: formData.genre,
          price: formData.is_free ? 0 : formData.price,
          is_free: formData.is_free,
          file_url: uploadedFile.uploadedPath,
          file_size: uploadedFile.uploadedSize,
          album: formData.album,
          album_cover_url: formData.album_cover_url,
          spotify_track_id: selectedSpotifyTrack?.id,
          status: 'pending_review'
        })
        .select()
        .single();

      if (sequenceError) throw sequenceError;

      toast.success('Sequence submitted successfully!');
      
      if (onSuccess && sequence) {
        onSuccess(sequence.id);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit sequence. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // State
    currentStep,
    uploadedFile,
    selectedSpotifyTrack,
    uploadProgress,
    isUploading,
    isSubmitting,
    form,
    
    // Navigation
    getCurrentStepId,
    goToStep,
    goNext,
    goBack,
    
    // Actions
    handleFileSelect,
    handleFileRemove,
    handleSpotifyTrackSelect,
    handleSubmit,
    onCancel,
    
    // Computed
    totalSteps: WIZARD_STEPS.length,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === WIZARD_STEPS.length - 1,
    currentStepInfo: WIZARD_STEPS[currentStep],
  };
};